import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Link2, Code, Copy, Trash2, Plus, ExternalLink, BarChart3, Eye, Settings2, Monitor,
} from "lucide-react";

const DOMAIN = "sorgupanel.org";

function copy(text) {
  navigator.clipboard.writeText(text);
  toast.success("Panoya kopyalandı!");
}

function LinksTab() {
  const [links, setLinks] = useState([]);
  const [url, setUrl] = useState("");
  const [code, setCode] = useState("");
  const [loading, setLoading] = useState(false);

  const load = () => api.get("/links").then((r) => setLinks(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const create = async (e) => {
    e.preventDefault();
    if (!url.trim()) return;
    setLoading(true);
    try {
      await api.post("/links", { target_url: url, custom_code: code || null });
      toast.success("Kısa link oluşturuldu");
      setUrl(""); setCode(""); load();
    } catch (err) { toast.error(apiErr(err)); }
    finally { setLoading(false); }
  };

  const remove = async (id) => {
    await api.delete(`/links/${id}`);
    toast.success("Link silindi"); load();
  };

  return (
    <div className="space-y-6">
      <form onSubmit={create} className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl p-6 space-y-4">
        <h3 className="font-display font-semibold text-lg flex items-center gap-2"><Link2 className="h-5 w-5 text-blue-600" /> Yeni Kısa Link</h3>
        <Input data-testid="link-url-input" value={url} onChange={(e) => setUrl(e.target.value)} placeholder="Uzun URL (https://...)" className="h-11" />
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="flex items-center gap-2 flex-1 rounded-lg border border-slate-200 dark:border-slate-700 px-3 h-11 bg-slate-50 dark:bg-slate-800/50">
            <span className="text-sm text-slate-400 font-mono shrink-0">{DOMAIN}/s/</span>
            <input data-testid="link-code-input" value={code} onChange={(e) => setCode(e.target.value)} placeholder="özel-kod (opsiyonel)" className="bg-transparent outline-none text-sm font-mono flex-1 w-full" />
          </div>
          <Button data-testid="create-link-btn" type="submit" disabled={loading} className="h-11 bg-blue-600 hover:bg-blue-700"><Plus className="h-4 w-4 mr-1" /> Oluştur</Button>
        </div>
      </form>

      <div className="space-y-3">
        {links.length === 0 && <p className="text-center text-slate-400 py-8">Henüz kısa link yok.</p>}
        {links.map((l) => (
          <div key={l.id} data-testid={`link-row-${l.id}`} className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl p-4 flex items-center gap-4">
            <div className="h-10 w-10 rounded-lg bg-blue-50 dark:bg-blue-950/40 grid place-items-center shrink-0"><Link2 className="h-5 w-5 text-blue-600" /></div>
            <div className="min-w-0 flex-1">
              <div className="font-mono text-sm font-semibold text-blue-600 truncate">{l.short_url}</div>
              <div className="text-xs text-slate-400 truncate">{l.target_url}</div>
            </div>
            <div className="flex items-center gap-1 text-xs text-slate-500 font-medium shrink-0"><BarChart3 className="h-3.5 w-3.5" /> {l.clicks}</div>
            <button onClick={() => copy(l.short_url)} data-testid={`copy-link-${l.id}`} className="p-2 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 text-slate-500"><Copy className="h-4 w-4" /></button>
            <button onClick={() => remove(l.id)} data-testid={`delete-link-${l.id}`} className="p-2 rounded-lg hover:bg-red-50 text-red-500"><Trash2 className="h-4 w-4" /></button>
          </div>
        ))}
      </div>
    </div>
  );
}

const DEFAULT_HTML = `<!DOCTYPE html>
<html lang="tr">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>Sayfam</title>
</head>
<body style="font-family:sans-serif;text-align:center;padding:60px;">
  <h1>Merhaba SorguPanel!</h1>
  <p>Bu benim özel HTML sayfam.</p>
</body>
</html>`;

function emptySite() {
  return { title: "", slug: "", html: DEFAULT_HTML, published: true,
    seo: { meta_description: "", meta_keywords: "", og_image: "", canonical: "", noindex: false } };
}

function SitesTab() {
  const [sites, setSites] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptySite());
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/sites").then((r) => setSites(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const openNew = () => { setEditing(null); setForm(emptySite()); setOpen(true); };
  const openEdit = (s) => { setEditing(s); setForm({ ...s, seo: s.seo || emptySite().seo }); setOpen(true); };

  const save = async () => {
    if (!form.title.trim() || !form.slug.trim()) { toast.error("Başlık ve slug gerekli"); return; }
    setSaving(true);
    try {
      if (editing) {
        await api.put(`/sites/${editing.id}`, form);
        toast.success("Sayfa güncellendi");
      } else {
        await api.post("/sites", form);
        toast.success("Sayfa yayınlandı");
      }
      setOpen(false); load();
    } catch (err) { toast.error(apiErr(err)); }
    finally { setSaving(false); }
  };

  const remove = async (id) => {
    await api.delete(`/sites/${id}`);
    toast.success("Sayfa silindi"); load();
  };

  const setSeo = (k, v) => setForm((f) => ({ ...f, seo: { ...f.seo, [k]: v } }));

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <p className="text-sm text-slate-500">Kendi HTML sayfanızı özel slug ile yayınlayın: <span className="font-mono text-blue-600">{DOMAIN}/belirle</span></p>
        <Button data-testid="new-site-btn" onClick={openNew} className="bg-blue-600 hover:bg-blue-700"><Plus className="h-4 w-4 mr-1" /> Yeni Sayfa</Button>
      </div>

      <div className="grid gap-3 sm:grid-cols-2">
        {sites.length === 0 && <p className="text-center text-slate-400 py-8 col-span-2">Henüz HTML sayfası yok.</p>}
        {sites.map((s) => (
          <div key={s.id} data-testid={`site-row-${s.id}`} className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl p-5">
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0">
                <h4 className="font-semibold text-slate-900 dark:text-white truncate">{s.title}</h4>
                <a href={s.url} target="_blank" rel="noreferrer" className="text-xs font-mono text-blue-600 inline-flex items-center gap-1 hover:underline">{DOMAIN}/{s.slug} <ExternalLink className="h-3 w-3" /></a>
              </div>
              {s.published ? <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-emerald-50 text-emerald-600 shrink-0">YAYINDA</span> : <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-slate-100 text-slate-500 shrink-0">TASLAK</span>}
            </div>
            <div className="flex items-center gap-3 mt-4 text-xs text-slate-400">
              <span className="inline-flex items-center gap-1"><Eye className="h-3.5 w-3.5" /> {s.views} görüntülenme</span>
              {s.seo?.noindex && <span className="text-amber-600">noindex</span>}
            </div>
            <div className="flex gap-2 mt-4">
              <Button variant="outline" size="sm" data-testid={`edit-site-${s.id}`} onClick={() => openEdit(s)}>Düzenle</Button>
              <Button variant="ghost" size="sm" data-testid={`delete-site-${s.id}`} onClick={() => remove(s.id)} className="text-red-500 hover:text-red-600"><Trash2 className="h-4 w-4" /></Button>
            </div>
          </div>
        ))}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="site-dialog">
          <DialogHeader><DialogTitle className="font-display">{editing ? "Sayfayı Düzenle" : "Yeni HTML Sayfa"}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="grid sm:grid-cols-2 gap-3">
              <div>
                <label className="text-sm font-medium mb-1.5 block">Sayfa Başlığı</label>
                <Input data-testid="site-title-input" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="Sayfam" />
              </div>
              <div>
                <label className="text-sm font-medium mb-1.5 block">Slug (URL)</label>
                <div className="flex items-center gap-1 rounded-lg border border-slate-200 dark:border-slate-700 px-2 h-10 bg-slate-50 dark:bg-slate-800/50">
                  <span className="text-xs text-slate-400 font-mono shrink-0">/</span>
                  <input data-testid="site-slug-input" value={form.slug} onChange={(e) => setForm({ ...form, slug: e.target.value.toLowerCase() })} placeholder="belirle" className="bg-transparent outline-none text-sm font-mono flex-1 w-full" />
                </div>
              </div>
            </div>
            <div>
              <label className="text-sm font-medium mb-1.5 block">HTML Kodu</label>
              <Textarea data-testid="site-html-input" value={form.html} onChange={(e) => setForm({ ...form, html: e.target.value })} rows={9} className="font-mono text-xs" />
            </div>

            <div>
              <label className="text-sm font-medium mb-1.5 flex items-center gap-1.5"><Monitor className="h-4 w-4 text-blue-600" /> Canlı Önizleme</label>
              <div className="rounded-lg border border-slate-200 dark:border-slate-700 overflow-hidden bg-white">
                <iframe
                  title="site-preview"
                  data-testid="site-preview"
                  srcDoc={form.html}
                  sandbox="allow-same-origin"
                  className="w-full h-56 bg-white"
                />
              </div>
            </div>

            <div className="rounded-xl border border-slate-200 dark:border-slate-800 p-4 space-y-3">
              <div className="flex items-center gap-2 text-sm font-semibold"><Settings2 className="h-4 w-4 text-blue-600" /> Sayfa SEO Ayarları</div>
              <div>
                <label className="text-xs text-slate-500 mb-1 block">Meta Açıklama</label>
                <Input data-testid="site-meta-desc" value={form.seo.meta_description} onChange={(e) => setSeo("meta_description", e.target.value)} placeholder="Sayfa açıklaması (SEO)" />
              </div>
              <div className="grid sm:grid-cols-2 gap-3">
                <div>
                  <label className="text-xs text-slate-500 mb-1 block">Anahtar Kelimeler</label>
                  <Input data-testid="site-meta-keywords" value={form.seo.meta_keywords} onChange={(e) => setSeo("meta_keywords", e.target.value)} placeholder="kelime1, kelime2" />
                </div>
                <div>
                  <label className="text-xs text-slate-500 mb-1 block">Canonical URL</label>
                  <Input data-testid="site-canonical" value={form.seo.canonical} onChange={(e) => setSeo("canonical", e.target.value)} placeholder="https://..." />
                </div>
              </div>
              <div className="flex items-center justify-between pt-1">
                <div>
                  <div className="text-sm font-medium">Yayında</div>
                  <div className="text-xs text-slate-400">Sayfayı herkese açık yap</div>
                </div>
                <Switch data-testid="site-published-switch" checked={form.published} onCheckedChange={(v) => setForm({ ...form, published: v })} />
              </div>
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-sm font-medium">noindex</div>
                  <div className="text-xs text-slate-400">Arama motorlarında gizle</div>
                </div>
                <Switch data-testid="site-noindex-switch" checked={form.seo.noindex} onCheckedChange={(v) => setSeo("noindex", v)} />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>İptal</Button>
            <Button data-testid="save-site-btn" onClick={save} disabled={saving} className="bg-blue-600 hover:bg-blue-700">{saving ? "Kaydediliyor..." : (editing ? "Güncelle" : "Yayınla")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default function Dashboard() {
  const { user } = useAuth();
  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 py-10">
      <div className="mb-8">
        <h1 className="font-display text-2xl sm:text-3xl font-bold text-slate-900 dark:text-white">Panelim</h1>
        <p className="text-slate-500 mt-1">Hoş geldin, {user?.name}. Araçlarını buradan yönet.</p>
      </div>

      <Tabs defaultValue="links">
        <TabsList className="mb-6">
          <TabsTrigger value="links" data-testid="tab-links"><Link2 className="h-4 w-4 mr-1.5" /> Link Kısaltıcı</TabsTrigger>
          <TabsTrigger value="sites" data-testid="tab-sites"><Code className="h-4 w-4 mr-1.5" /> HTML Site</TabsTrigger>
        </TabsList>
        <TabsContent value="links"><LinksTab /></TabsContent>
        <TabsContent value="sites"><SitesTab /></TabsContent>
      </Tabs>
    </div>
  );
}
